import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
} from '@mui/material';
import _ from 'lodash';
import { useState } from 'react';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { WorkflowProposalProcess } from '~/components/WorkflowProposalProcess';
import {
  ApprovalStepState,
  ProposalPreviewProps,
  ProposalProps,
  ProposalStatus,
  ProposalType,
} from '~/data/proposals';
import { EmployeeProposalStatus } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/EmployeeProposalStatus';
import { EmployeeProposalUpdateCallback } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { useEmployeeProposal } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/useEmployeeProposal';
import {
  BillableHoursProposalFormProps,
  BonusProposalFormProps,
  ProposalFormProps,
  SalaryProposalFormProps,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/Common';
import { isRoleHRAdmin } from '~/services/Permissions';
import { getLastInArray } from '~/utils/helper';

import { EmployeeProposalDeleteButton } from './components/EmployeeProposalDeleteButton';
import {
  AllowanceProposalForm,
  BenefitsProposalForm,
  BillableHoursProposalForm,
  BonusProposalForm,
  SalaryProposalForm,
} from './components/Forms';
import { useSxStyles } from './useStyles';

type PropsType = {
  proposal?: ProposalProps;
  newProposalType?: ProposalType;
  proposalPreview?: ProposalPreviewProps;
  employeeId: number;
  defaultExpanded?: boolean;
  onProposalUpdate: EmployeeProposalUpdateCallback;
  formProps?: ProposalFormProps;
};

export const EmployeeProposal = (props: PropsType) => {
  const {
    proposal,
    newProposalType,
    employeeId,
    defaultExpanded,
    onProposalUpdate,
    formProps,
  } = props;
  const sxStyles = useSxStyles();

  const { handleDecision, handleUltimateDecision, formRef } =
    useEmployeeProposal();

  const renderProposalForm = (proposalData?: ProposalProps) => {
    const commonProps = {
      employeeId,
      isNewProposal: !!newProposalType,
      onProposalUpdate,
      ref: formRef,
    };

    switch (proposalData?.proposalType) {
      case ProposalType.ALLOWANCE_PROPOSAL:
        return (
          <AllowanceProposalForm proposal={proposalData} {...commonProps} />
        );
      case ProposalType.BENEFIT_PROPOSAL:
        return (
          <BenefitsProposalForm proposal={proposalData} {...commonProps} />
        );
      case ProposalType.BILLABLE_HOURS:
        return (
          <BillableHoursProposalForm
            proposal={proposalData}
            {...commonProps}
            formProps={formProps as BillableHoursProposalFormProps}
          />
        );
      case ProposalType.BONUS_PROPOSAL:
        return (
          <BonusProposalForm
            proposal={proposalData}
            {...commonProps}
            formProps={formProps as BonusProposalFormProps}
          />
        );
      case ProposalType.SALARY_PROPOSAL:
        return (
          <SalaryProposalForm
            proposal={proposalData}
            formProps={formProps as SalaryProposalFormProps}
            {...commonProps}
          />
        );
      default:
        return null;
    }
  };

  const lockExpanded = !!newProposalType;
  const initialExpandedState = (lockExpanded || defaultExpanded) ?? false;

  // Toggle proposal open/collapsed
  const [expanded, setExpanded] = useState(initialExpandedState);

  // For performance, don't render collapsed proposals content by default
  const [formInitiated, setFormInitiated] = useState(initialExpandedState);

  const handleExpanded = () => {
    if (!lockExpanded) {
      setExpanded(!expanded);
    }
    if (!formInitiated) {
      setFormInitiated(true);
    }
  };

  const isPreview = proposal?.proposalStatus === ProposalStatus.PROPOSED;

  const loggedInUserRoles = useLoggedInUserRoles();
  const loggedInEmployeeId = useLoggedInEmployeeId();
  const { disabledDeleteProposals = [] } =
    (formProps as SalaryProposalFormProps) || ({} as SalaryProposalFormProps);

  const showProposalButton = (proposal: ProposalProps): boolean => {
    if (proposal.proposalStatus !== ProposalStatus.COMPLETED) {
      return false;
    }
    const finalApprovalStep = getLastInArray<ApprovalStepState>(
      proposal.approvalSteps,
    );

    const isLoggedInUserFinalApprover = !!finalApprovalStep?.approvers.find(
      (approver) => approver.userId === loggedInEmployeeId,
    );

    const showButton =
      isRoleHRAdmin(loggedInUserRoles) || isLoggedInUserFinalApprover;

    if (!_.isEmpty(disabledDeleteProposals)) {
      return (
        !disabledDeleteProposals.includes(proposal.proposalId) && showButton
      );
    }

    return showButton;
  };
  return !!proposal ? (
    <Accordion expanded={expanded} onChange={handleExpanded}>
      <AccordionSummary
        expandIcon={!lockExpanded && <ExpandMoreIcon />}
        sx={sxStyles.accordionSummary}
        className={lockExpanded ? 'lockedExpanded' : ''}
      >
        <Box sx={sxStyles.accordionSummaryItems}>
          <EmployeeProposalStatus proposal={proposal} />
          {showProposalButton(proposal) && (
            <EmployeeProposalDeleteButton
              proposal={proposal}
              proposalUpdateCallback={onProposalUpdate}
            />
          )}
        </Box>
      </AccordionSummary>
      <Divider />
      <AccordionDetails sx={sxStyles.accordionDetails}>
        {formInitiated && (
          <Box sx={sxStyles.wrapper}>
            <Box className={'form'}>{renderProposalForm(proposal)}</Box>
            <Box className={'approvals'}>
              {proposal && (
                <WorkflowProposalProcess
                  proposal={proposal}
                  onDecision={handleDecision}
                  onUltimateDecision={handleUltimateDecision}
                  isPreview={isPreview}
                  autoCompleteOnResponsibleHR={true}
                />
              )}
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  ) : (
    <></>
  );
};
